import React, { useEffect, useLayoutEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { DFPSlotsProvider, DFPManager } from '@dsch/react-dfp';
import { useInView } from 'react-intersection-observer';
import { ShieldCheckmark2Ic } from '@dsch/dd-icons';

import { formatNumber, formatAddress } from 'helpers/formatting';
import { media } from 'helpers/breakpoints';
import { useAdDetailsContext } from 'contexts/AdDetailsContext';
import { MiniUserProfile } from 'components/Toolkit/MiniUserProfile/MiniUserProfile';
import { HtmlInfoPanel } from 'components/Toolkit/HtmlInfoPanel/HtmlInfoPanel';
import { LegalInfoPanel } from 'components/Toolkit/LegalInfoPanel/LegalInfoPanel';
import { AdTitleBox } from 'components/PageWrappers/AdDetails/components/AdTitleBox/AdTitleBox';
import { SellerInfoPanel } from 'components/PageWrappers/AdDetails/components/SellerInfoPanel/SellerInfoPanel';
import { SideSellerInfoPanel } from 'components/PageWrappers/AdDetails/components/SideSellerInfoPanel/SideSellerInfoPanel';
import { GreenlightPanel } from 'components/PageWrappers/AdDetails/components/GreenlightPanel/GreenlightPanel';
import { InfoPanel } from 'components/PageWrappers/AdDetails/components/InfoPanel/InfoPanel';
import { RevealPhoneNumber } from 'components/PageWrappers/SharedComponents/RevealPhoneNumber/RevealPhoneNumber';
import { ShowAndToggleSaveAd } from 'components/PageWrappers/SharedComponents/ShowAndToggleSaveAd/ShowAndToggleSaveAd';
import { RevealReportAd } from 'components/PageWrappers/AdDetails/components/RevealReportAd/RevealReportAd';
import { TrustedDealerBadge } from 'components/PageWrappers/SharedComponents/TrustedDealerBadge/TrustedDealerBadge';
import { BreadCrumbs } from 'components/Toolkit/BreadCrumbs/BreadCrumbs';
import { DealerPanel } from 'components/Toolkit/DealerPanel/DealerPanel';
import type { BreadCrumb } from 'components/Toolkit/BreadCrumbs/BreadCrumbs';
import {
  VideoConfig,
  PhotoView,
  AdSummary,
  ISeoLinks,
  DataTargetingProps,
  PromoSlot,
  IFinanceBanner,
  CURRENCIES,
} from 'types';
import { RevealSendMessage } from 'components/PageWrappers/SharedComponents/RevealSendMessage/RevealSendMessage';
import { RevealDealerEnquiryModal } from 'components/PageWrappers/SharedComponents/RevealDealerEnquiryModal/RevealDealerEnquiryModal';
import {
  DFPUnit,
  refreshAdUnits,
  getTargetingArguments,
} from 'components/Toolkit/DFP/DFP';
import { useUserContext } from 'contexts/UserContext';
import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { ShareAd } from 'components/Toolkit/ShareAd/ShareAd';
import { Button } from 'components/Toolkit/Button/Button';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { AD_DETAILS_DFP_CONFIG } from 'configs/dfpConfig';
import { AD_DETAILS_AD_SENSE_CONFIG } from 'configs/adSenseConfig';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';
import { InternalLinks } from 'components/Toolkit/InternalLinks/InternalLinks';
import { PromoPanelCollection } from 'components/PageWrappers/AdDetails/components/PromoPanelCollection/PromoPanelCollection';
import { OptionalNextLink } from 'components/Toolkit/OptionalNextLink/OptionalNextLink';
import { FeaturedDealer } from 'components/Toolkit/FeaturedDealer/FeaturedDealer';
import { SpecialInfoPanel } from 'components/Toolkit/SpecialInfoPanel/SpecialInfoPanel';

import type { AdDetailsComponentsThatAreNotInitiallyVisibleProps } from 'components/PageWrappers/AdDetails/components/Layouts/AdDetailsComponentsThatAreNotInitiallyVisible';
import type { RevealGalleryProps } from 'components/PageWrappers/AdDetails/components/RevealGallery/RevealGallery';
import type { GalleryPlaceholderProps } from 'components/PageWrappers/AdDetails/components/GalleryPlaceholder/GalleryPlaceholder';

import type {
  SectionView,
  ServicePartner,
  DealerSummary,
  CardPayment,
  FinanceInfo,
  FinanceLink,
  GreenlightView,
  UserSummary,
  ApprovedProgrammeView,
} from 'components/PageWrappers/AdDetails/types';
import { Currency, IAdAttributeView } from 'types';
import { RelatedAdsGrid } from 'components/PageWrappers/AdDetails/components/RelatedAds/RelatedAdsGrid';
import { Link } from 'components/Toolkit/Button/Link';
import { SimpleErrorBoundary } from 'helpers/SimpleErrorBoundary';
import { AdminPanel } from 'components/PageWrappers/AdDetails/components/AdminPanel/AdminPanel';
import { RevealSaveSearchPanel } from 'components/PageWrappers/SharedComponents/RevealSaveSearchPanel/RevealSaveSearchPanel';
import { getSellerProfileImage } from 'helpers/sellerProfile';
import { pushToDataLayer } from 'services/gtmService';
import { InfoTitleStarRating } from 'components/PageWrappers/AdDetails/components/GoogleReviews/InfoTitleStarRating/InfoTitleStarRating';
import { StickyFooter } from 'components/Toolkit/StickyFooter/StickyFooter';
import { Columns } from 'components/Toolkit/Columns/Columns';
import { ElectricCarsHubPanel } from 'components/PageWrappers/AdDetails/components/ElectricCarsHubPanel/ElectricCarsHubPanel';

import { generateSaveSearchModalData } from 'components/PageWrappers/AdDetails/helpers';
import { useCreateSavedSearch } from 'components/SearchPage/hooks/useCreateSavedSearch';
import { useAdSense } from 'hooks/UseAdSense';

import { adSenseAdDetailsStyleId } from 'helpers/constants';

import { AdSenseUnit } from 'components/Toolkit/AdSenseUnit/AdSenseUnit';

import { UseBoostHandler } from 'hooks/UseBoostHandler';
import { countiesNI } from 'constants/counties';
import { Notification } from 'components/Toolkit/Notification/Notification';
import { formatAuthorizationHeader } from 'helpers/auth';
import { useAuthOptions } from 'hooks/useAuthOptions';

import type { VehicleFeatures } from 'components/PageWrappers/AdDetails/components/VehicleFeatures/VehicleFeatures.typed';
import { mapVehicleFeatures } from 'components/PageWrappers/AdDetails/components/VehicleFeatures/helpers/index';
import { PAGE } from 'helpers/pages';
import { renderTrustedDealerInSection } from 'helpers/trustedDealerProgram';
import { ContactDealership } from 'components/PageWrappers/AdDetails/components/ContactDealership/ContactDealership';
import { rg4js } from 'helpers/raygun';

export interface AdDetailsProps {
  displayExpiredForSeo?: boolean;
  state?: string;
  dfpNetworkId: string;
  environmentName: string;
  baseUrl: string;
  cdnUrl: string;
  seller?: UserSummary;
  dealer?: DealerSummary;
  buyerTips?: {
    title: string;
    values: string[];
  };
  traderName?: string;
  publisherName?: string;
  header: string;
  description: string;
  currency: Currency;
  price: string | null;
  oldPriceView?: {
    oldPrice?: string | null;
    showOldPrice: boolean;
  };
  age?: string;
  views?: number;
  county?: string;
  id: number;
  emailResponse: boolean;
  phoneResponse: boolean;
  userSaved?: boolean;
  photos?: PhotoView[];
  link360?: string;
  link360Interior?: string;
  videoConfig?: VideoConfig;
  mediaCount?: number;
  section?: SectionView;
  breadcrumbs?: SectionView[];
  friendlyUrl?: string;
  stripeEnabled: boolean;
  depositEnabled: boolean;
  deliveryAvailable?: boolean;
  delivery?: {
    fee?: number;
    info?: string;
  };
  quantity?: number;
  buyNowSuccess?: boolean;
  boostInfoList?: {
    type: string;
    header: string;
    lines: string[];
    price: string;
  }[];
  youTubeVideoId?: string;
  youTubeReviewVideoId?: string;
  dataTargetingProps: DataTargetingProps;
  seoData?: ISeoLinks;
  displayAttributes?: IAdAttributeView[];
  servicePartners?: ServicePartner[];
  relatedAds?: AdSummary[];
  promoSlot?: PromoSlot;
  cardPayments?: CardPayment[];
  manufacturerApprovedBadge?: { manufacturer: string };
  meritsV2?: { field: string; text: string }[];
  herdwatchVerified?: boolean;
  specialOffer?: string;
  financeInfo: FinanceInfo | null;
  greenlightVerified?: boolean;
  greenlight?: GreenlightView;
  showHistorycheckV2?: boolean;
  campaignId?: string | null;
  financeLink?: FinanceLink;
  approvedProgramme?: ApprovedProgrammeView;
  countyTown?: string;
  wanted?: boolean;
  spotlight?: boolean;
  isAdmin?: boolean;
  finance?: {
    bannerUrl: string;
    banner?: IFinanceBanner;
  };
  buttonDisplayText?: string;
  isPreview?: boolean;
  adSensePublicKey: string;
  adAnalytics?: boolean;
  triggerSaveAdPulseOnLoad?: boolean;
  priceOnRequest?: boolean;
  vehicleFeatures?: VehicleFeatures;
  whatsappResponse: boolean;
}

interface ISMiniUserProfile {
  isDealer: boolean;
}

const AdDetailsComponentsThatAreNotInitiallyVisible =
  dynamic<AdDetailsComponentsThatAreNotInitiallyVisibleProps>(
    () =>
      import(
        'components/PageWrappers/AdDetails/components/Layouts/AdDetailsComponentsThatAreNotInitiallyVisible'
      ).then((mod) => mod.AdDetailsComponentsThatAreNotInitiallyVisible),
    { ssr: false },
  );

const RevealGallery = dynamic<RevealGalleryProps>(
  import('./components/RevealGallery/RevealGallery').then(
    (mod) => mod.RevealGallery,
  ),
);

const GalleryPlaceholder = dynamic<GalleryPlaceholderProps>(
  import('./components/GalleryPlaceholder/GalleryPlaceholder').then(
    (mod) => mod.GalleryPlaceholder,
  ),
);

export const SMiniUserProfile = styled(MiniUserProfile)<ISMiniUserProfile>`
  background-color: ${({ theme }) => theme.colors.WHITE};
  padding: ${({ theme }) => `${theme.spacing.S12} ${theme.spacing.M16}`};
  border-radius: ${({ theme }) => `${theme.borderRadius.default}
    ${theme.borderRadius.default} 0 0`};
  border-bottom: ${({ theme }) => `1px solid ${theme.colors.GREY_LIGHTER}`};
  ${media.large} {
    ${({ isDealer }) => !isDealer && 'display: none;'}
    padding: ${({ theme }) => `${theme.spacing.S12} ${theme.spacing.M24}`};
  }
  display: flex;
`;

export const MiniUserProfileAndGalleryContainer = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.default};
  box-shadow: ${({ theme }) => theme.shadow.CONTAINER};
`;

const SHtmlInfoPanel = styled(HtmlInfoPanel)`
  display: none;

  ${media.large} {
    display: initial;
  }
`;

const SLegalInfoPanel = styled(LegalInfoPanel)`
  /* We give legal a little extra room with some padding */
  padding-top: ${({ theme }) => theme.spacing.M16};
  margin: ${({ theme }) => `0 ${theme.spacing.S8} ${theme.spacing.M24}`};

  ${media.large} {
    padding-top: ${({ theme }) => theme.spacing.M16};
    margin: ${({ theme }) => `0 ${theme.spacing.S8} ${theme.spacing.M32}`};
  }
`;

const SideColumn = styled.div`
  display: none;

  ${media.large} {
    display: block;
    padding-bottom: 24px;
  }
`;

const SSideSellerInfoPanel = styled(SideSellerInfoPanel)`
  display: none;

  ${media.large} {
    display: block;
  }
`;

const BreadcrumbContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.GREY_LIGHTER};
  box-shadow: ${({ theme }) => theme.shadow.CONTAINER};
`;

const AdDetailsWrapper = styled.main`
  .mid-placement-extra-space {
    padding: ${({ theme }) => theme.spacing.S8} 0;
    ${media.large} {
      padding: 0;
    }
  }

  .fullscreen-placement-overlay {
    position: fixed;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 9000;
  }
`;

const RelatedAds = styled.div<{ keepPaddingOnDesktop: boolean }>`
  padding: 0 ${({ theme }) => theme.spacing.S8};

  ${media.large} {
    padding: 0
      ${({ theme, keepPaddingOnDesktop }) =>
        keepPaddingOnDesktop ? theme.spacing.S8 : 0};
  }
`;

const OffWhiteWrapper = styled.div<{ showBackground: boolean }>`
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background-color: ${({ theme, showBackground }) =>
    showBackground ? theme.colors.OFFWHITE : 'none'};
`;

const OurStockHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.S8};
  display: flex;
  justify-content: space-between;
`;

const SNotification = styled(Notification)`
  ${({ theme }) => theme.fontSize.S12};
  line-height: ${({ theme }) => theme.spacing.M16};
  margin-right: ${({ theme }) => theme.spacing.S8};
  ${media.large} {
    ${({ theme }) => theme.fontSize.M16};
    svg {
      margin-right: ${({ theme }) => theme.spacing.S8};
    }
    padding: ${({ theme }) => theme.spacing.M16};
  }
`;

const OurStockHeading = styled.div`
  ${({ theme }) => theme.fontSize.L18};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const SeeAllStock = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.M16} 0;
`;

const SInternalLinks = styled(InternalLinks)`
  background-color: ${({ theme }) => theme.colors.WHITE};
  padding: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    padding: ${({ theme }) => theme.spacing.M24} 0;
  }
`;

const SeoContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.WHITE};
`;

const FeaturedDealerContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.GREY_DARKER};

  ${media.large} {
    padding: ${({ theme }) => `${theme.spacing.M32} 0`};
  }
`;

const Sticky = styled.div`
  @media (min-height: 900px) {
    position: sticky;
    top: 0;
  }
`;

const MobileStickyFooter = styled(StickyFooter)<{
  isViewingOwnAd: boolean;
  whatsappResponse: boolean;
}>`
  z-index: ${({ isViewingOwnAd }) => (isViewingOwnAd ? 50 : 0)};

  ${media.large} {
    display: none;
  }

  ${({ whatsappResponse }) =>
    whatsappResponse &&
    `
   ${media.smallOnly} {
    display: none;
  }
  `}
`;

export const SGalleryPlaceholder = styled(GalleryPlaceholder)<{
  isDealer: boolean;
}>`
  border-radius: ${({ theme }) =>
    `0 0 ${theme.borderRadius.default} ${theme.borderRadius.default}`};

  ${media.large} {
    border-radius: ${({ isDealer, theme }) =>
      isDealer
        ? `0 0 ${theme.borderRadius.default} ${theme.borderRadius.default}`
        : theme.borderRadius.default};
  }
`;

const SShieldCheckmark2Ic = styled(ShieldCheckmark2Ic)`
  margin-left: ${({ theme }) => theme.spacing.M16};

  ${media.smallOnly} {
    width: 32px;
    height: 32px;
  }
`;

function AdDetails({
  displayExpiredForSeo = false,
  baseUrl,
  cdnUrl,
  seller,
  dealer,
  buyerTips,
  header,
  description,
  currency,
  price,
  oldPriceView,
  age,
  views,
  county,
  id,
  phoneResponse,
  emailResponse,
  userSaved,
  breadcrumbs,
  section,
  photos,
  link360,
  link360Interior,
  videoConfig,
  mediaCount = 0,
  stripeEnabled,
  depositEnabled,
  deliveryAvailable,
  delivery,
  dfpNetworkId,
  youTubeVideoId,
  youTubeReviewVideoId,
  friendlyUrl,
  quantity,
  buyNowSuccess,
  dataTargetingProps,
  displayAttributes,
  servicePartners,
  relatedAds,
  promoSlot,
  seoData,
  manufacturerApprovedBadge,
  specialOffer,
  meritsV2,
  herdwatchVerified,
  cardPayments,
  financeInfo,
  greenlightVerified,
  financeLink,
  greenlight,
  showHistorycheckV2,
  campaignId,
  approvedProgramme,
  countyTown,
  wanted,
  publisherName,
  isAdmin,
  finance,
  buttonDisplayText,
  isPreview,
  adSensePublicKey,
  adAnalytics,
  triggerSaveAdPulseOnLoad = true,
  priceOnRequest = false,
  vehicleFeatures,
  state,
  whatsappResponse,
}: AdDetailsProps) {
  const isSold = state === 'Sold';
  const theme = useTheme();
  const { accessToken } = useAuthOptions();

  const {
    isAdSaved,
    setIsAdSaved,
    setIsShareModalOpen,
    isGalleryOpen,
    setIsGalleryOpen,
    isYoutubeReviewOpen,
    setIsYoutubeReviewOpen,
  } = useAdDetailsContext();
  const {
    user,
    setIsBoostModalOpen,
    setBoostModalData,
    consentOptions: { allowGoogle },
  } = useUserContext();

  const [imageCounterForDFP, setImageCounterForDFP] = useState<number>(1);
  const [hasSavedAdAnimationOccurred, setHasSavedAdAnimationOccurred] =
    useState(false);

  const {
    createSavedSearchFromAd,
    saving: savedSearchSaving,
    error: savedSearchError,
    saved: savedSearchSaved,
    setSaved: setIsSearchSaved,
  } = useCreateSavedSearch({ accessToken });

  useAdSense({
    options: {
      pubId: adSensePublicKey,
      styleId: adSenseAdDetailsStyleId,
      query: header,
      location: false,
      siteLinks: false,
    },
    dependencies: [allowGoogle],
    units: [AD_DETAILS_AD_SENSE_CONFIG.middle],
  });

  useOnUpdateOnly(() => {
    try {
      DFPManager.setTargetingArguments({
        ...dataTargetingProps.dfp,
        ...getTargetingArguments(),
      });

      DFPManager.reload(
        AD_DETAILS_DFP_CONFIG.dd_detail_top.slotId,
        AD_DETAILS_DFP_CONFIG.dd_detail_mid.slotId,
        AD_DETAILS_DFP_CONFIG.dd_detail_side.slotId,
      );
    } catch (e) {
      rg4js('send', {
        error: e,
        message: 'Executing useOnUpdateOnly error line 530',
        metadata: {
          dataTargetingProps,
          targetingArguments: getTargetingArguments(),
        },
      });
    }
  }, [id]);

  /** Tracks number of images viewed in the Gallery and refreshes
  the associated DFP unit after any 3 images have been viewed */
  useEffect(() => {
    if (imageCounterForDFP === 4) {
      refreshAdUnits([AD_DETAILS_DFP_CONFIG.dd_detail_photobrowser.slotId]);
      setImageCounterForDFP(1);
    }
  }, [imageCounterForDFP]);

  const handleHistoryChangePushToDataLayer =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  handleHistoryChangePushToDataLayer(() => {
    dataTargetingProps.gtm && pushToDataLayer(dataTargetingProps.gtm);
  }, [dataTargetingProps.gtm]);

  handleHistoryChangePushToDataLayer(() => {
    displayExpiredForSeo && pushToDataLayer({ event: 'expired_ad_view' });
  }, []);

  useEffect(() => {
    try {
      window.addEventListener('message', messageReceivedFromDfpUnit);
      DFPManager.setTargetingArguments({
        ...dataTargetingProps.dfp,
        ...getTargetingArguments(),
      });

      return () => {
        window.removeEventListener('message', messageReceivedFromDfpUnit);
      };
    } catch (e) {
      rg4js('send', {
        error: e,
        message: 'Executing useEffect error line 571',
        metadata: {
          dataTargetingProps,
          targetingArguments: getTargetingArguments(),
        },
      });
    }
  }, []);

  useEffect(() => {
    if (isGalleryOpen === false) {
      setIsYoutubeReviewOpen(false);
    }
  }, [isGalleryOpen]);

  useOnUpdateOnly(() => {
    setIsAdSaved && userSaved !== undefined && setIsAdSaved(userSaved);
    setHasSavedAdAnimationOccurred && setHasSavedAdAnimationOccurred(false);
  }, [id]);

  const { ref } = useInView({
    triggerOnce: true,
    rootMargin: '80px',
  });

  const [mobileStickyCTARefOne, mobileStickyCTAinView2] = useInView({
    rootMargin: '20px',
  });

  const [mobileStickyCTARefTwo, mobileStickyCTAinView3] = useInView({
    rootMargin: '20px',
  });

  const [saveAdRef, saveAdInView] = useInView({
    initialInView: true,
  });

  const isViewingOwnAd = Boolean(user?.id === seller?.id);
  const editAdHREF = `${baseUrl}/update/${id}`;
  const showMiniShowroom = Boolean(dealer && dealer.showMiniShowroom);
  const isDealer = Boolean(dealer);
  const isTrader = Boolean(seller?.traderName);
  const sellerPhoto =
    isDealer && dealer?.logo?.small
      ? dealer.logo.small
      : getSellerProfileImage(seller?.verification);
  const sellerTitle =
    ((dealer?.name || seller?.traderName) ?? publisherName ?? seller?.name) ||
    'Unknown';
  const sellerType = isDealer ? 'DEALER' : seller?.type;
  const showGallery = Boolean(photos?.length || videoConfig);

  const eligibleForSaveSearch = section?.name === 'cars';
  const showElectricCarHubPanel = displayAttributes?.some(
    (attribute: IAdAttributeView) => attribute.value === 'Electric',
  );

  function mapBreadCrumbs(breadcrumbs: SectionView[]): BreadCrumb[] {
    return breadcrumbs.map((breadcrumb: SectionView) => {
      return {
        path: `/${breadcrumb.name!}`,
        displayName: breadcrumb.displayName!,
      };
    });
  }

  function messageReceivedFromDfpUnit(event: MessageEvent) {
    const isTrusted = event.origin.includes('.googlesyndication.com');
    const frameEl = document.getElementById(
      'google_ads_iframe_5512429/donedeal/dd_detail_calculator_0',
    );
    const frameEl1 = document.getElementById(
      'google_ads_iframe_5512429/donedeal/dd_detail_textlink_0',
    );

    const { data } = event;
    const { calculatorTextLinkOpen } = data;

    if (frameEl && data.hasOwnProperty('dimensions') && isTrusted) {
      const { dimensions } = data;
      frameEl.style.height = dimensions.height === '100vh' ? '100vh' : 'auto';
      frameEl.style.width = '100%';
    }

    if (frameEl && data.hasOwnProperty('showModal') && isTrusted) {
      if (data.showModal === true) {
        frameEl.className = 'fullscreen-placement-overlay';
      } else {
        frameEl.className = '';
      }
    }

    if (frameEl1 && data.hasOwnProperty('dimensions') && isTrusted) {
      frameEl1.style.width = '100%';
    }

    if (frameEl1 && data.hasOwnProperty('showModal') && isTrusted) {
      if (data.showModal === true) {
        frameEl1.className = 'fullscreen-placement-overlay';
        if (frameEl && calculatorTextLinkOpen) {
          frameEl.style.width = '0';
          frameEl.style.height = '0';
          frameEl.style.minWidth = '0';
        } else {
          if (frameEl) {
            frameEl.style.width = '100%';
            frameEl.style.height = '100vh';
            frameEl.style.minWidth = '100%';
          }
        }
      } else {
        frameEl1.className = '';
      }
    }
  }

  /** For use with Gallery and Post Message Modals on close */
  function refreshTopAndSideDFPUnit() {
    const adUnitsToRefresh = [
      AD_DETAILS_DFP_CONFIG.dd_detail_top.slotId,
      AD_DETAILS_DFP_CONFIG.dd_detail_side.slotId,
    ];

    refreshAdUnits(adUnitsToRefresh);
  }

  const breadCrumbsList = breadcrumbs && mapBreadCrumbs(breadcrumbs);

  const breadCrumbSectionName =
    breadcrumbs && breadcrumbs.length > 1 ? breadcrumbs[1].name : null;

  const similarAds =
    breadCrumbsList && !isPreview
      ? breadCrumbsList[breadCrumbsList.length - 1].path
      : null;

  const renderTrustedDealerBadge =
    dealer?.trustedDealer && renderTrustedDealerInSection(section?.name || '');

  return (
    <AdDetailsWrapper>
      <DFPSlotsProvider dfpNetworkId={dfpNetworkId}>
        {!isPreview && breadCrumbsList && (
          <BreadcrumbContainer className="common-margin-bottom">
            <div className="row">
              <div className="columns small-12">
                <BreadCrumbs breadcrumbs={breadCrumbsList} baseUrl={baseUrl} />
              </div>
            </div>
          </BreadcrumbContainer>
        )}

        {!isPreview && isAdmin && (
          <AdminPanel adId={id} userId={seller?.id} baseUrl={baseUrl} />
        )}

        {!isPreview && !displayExpiredForSeo && (
          <DFPUnit
            className="common-margin-bottom"
            {...AD_DETAILS_DFP_CONFIG.dd_detail_top}
          />
        )}

        {!isPreview && displayExpiredForSeo && similarAds && (
          <div className={'row'}>
            <div className="columns medium-12 large-8 large-offset-2">
              <SNotification
                className="common-margin-bottom"
                type={'ERROR'}
                gapVariant={'SMALL'}
                text={
                  <>
                    This item has been marked as sold, expired or removed by the
                    seller. <a href={similarAds}>View similar ads here.</a>
                  </>
                }
              />
            </div>
          </div>
        )}

        <div
          className={!isPreview ? 'row small-collapse medium-uncollapse' : ''}
        >
          <div className={!isPreview ? 'columns small-12 large-8' : ''}>
            <MiniUserProfileAndGalleryContainer>
              {(seller?.traderName ?? seller?.name) &&
                !displayExpiredForSeo && (
                  <SMiniUserProfile
                    title={sellerTitle}
                    illustration={sellerPhoto}
                    county={county}
                    isTrader={isTrader}
                    type={seller.type}
                    franchiseType={dealer?.franchiseType}
                    verification={seller.verification}
                    isDealer={Boolean(dealer)}
                    largeTruncationWidth={546}
                    subtitleComponent={
                      dealer ? (
                        <InfoTitleStarRating
                          rating={
                            dealer?.googleReview && dealer.googleReview?.rating
                          }
                          maxRating={dealer?.googleReview && 5}
                          isDealer={Boolean(dealer)}
                          franchiseType={dealer?.franchiseType}
                          county={county}
                          isTrader={isTrader}
                          verification={seller.verification}
                          type={seller.type}
                          ratingGroupDataTracking={
                            'google-reviews-rating-group'
                          }
                          variant="LARGE"
                          styleAsLink={true}
                        />
                      ) : null
                    }
                    renderAdditionalHeaderInfo={
                      renderTrustedDealerBadge && (
                        <TrustedDealerBadge
                          icon={
                            <SShieldCheckmark2Ic
                              width="24"
                              height="24"
                              color={theme.colors.GREEN}
                            />
                          }
                          pageName={PAGE.AD_DETAILS}
                        />
                      )
                    }
                  />
                )}
              {showGallery ? (
                <RevealGallery
                  cdnUrl={cdnUrl}
                  trackingId="ad-open-photo-gallery"
                  photo={photos && photos[0]}
                  photos={photos}
                  videoConfig={videoConfig}
                  link360={link360}
                  link360Interior={link360Interior}
                  mediaCount={mediaCount}
                  title={header}
                  price={price}
                  currency={currency}
                  oldPrice={
                    (oldPriceView?.showOldPrice && oldPriceView.oldPrice) ||
                    undefined
                  }
                  pricePerMonth={
                    financeInfo?.pricePerMonth.priceDisplay || undefined
                  }
                  renderSendMessage={
                    dealer ? (
                      <RevealDealerEnquiryModal
                        adId={id}
                        size="GROW"
                        id="dealer-enquiry-btn-gallery"
                      />
                    ) : (
                      emailResponse && (
                        <RevealSendMessage
                          adId={id}
                          size="GROW"
                          clickSource="gallery"
                        />
                      )
                    )
                  }
                  renderShareAd={
                    <ShareAd
                      handleShareAd={() => setIsShareModalOpen(true)}
                      isUnderlined={true}
                    />
                  }
                  renderSaveAd={
                    userSaved !== undefined && (
                      <ShowAndToggleSaveAd
                        adId={id}
                        isUnderlined={false}
                        isAdSaved={isAdSaved}
                        setIsAdSaved={setIsAdSaved}
                        isViewingOwnAd={isViewingOwnAd}
                        pulseOnLoad={triggerSaveAdPulseOnLoad}
                        subsection={section?.name}
                        sellerType={sellerType}
                      />
                    )
                  }
                  renderShowPhone={
                    phoneResponse && (
                      <RevealPhoneNumber
                        id={id}
                        showNumberTextVariant={true}
                        size="GROW"
                        trackingId="reveal-number-gallery"
                        clickSource="gallery"
                        isPrimaryButton={Boolean(!dealer && !emailResponse)}
                        campaignId={campaignId}
                        sectionName={section?.name}
                        sellerType={sellerType}
                      />
                    )
                  }
                  renderDfpUnit={
                    <DFPUnit
                      className="common-margin-bottom"
                      {...AD_DETAILS_DFP_CONFIG.dd_detail_photobrowser}
                    />
                  }
                  keyInfo={[age, `${formatNumber(views)} views`, county]}
                  youTubeVideoId={youTubeVideoId}
                  youTubeReviewVideoId={youTubeReviewVideoId}
                  isDealer={isDealer}
                  className="common-margin-bottom"
                  isGalleryOpen={isGalleryOpen}
                  setIsGalleryOpen={setIsGalleryOpen}
                  isYoutubeReviewOpen={isYoutubeReviewOpen}
                  setIsYoutubeReviewOpen={setIsYoutubeReviewOpen}
                  imageCounterForDFP={imageCounterForDFP}
                  setImageCounterForDFP={setImageCounterForDFP}
                  isPreview={isPreview}
                  refreshTopAndSideDFPUnit={refreshTopAndSideDFPUnit}
                />
              ) : (
                <SGalleryPlaceholder
                  isDealer={isDealer}
                  buttonLink={editAdHREF}
                  isViewingOwnAd={isViewingOwnAd}
                  className="common-margin-bottom"
                />
              )}
            </MiniUserProfileAndGalleryContainer>
            <AdTitleBox
              adId={id}
              wanted={wanted}
              title={header}
              price={price}
              currency={currency}
              cdnUrl={cdnUrl}
              financeInfo={financeInfo}
              financeLink={financeLink}
              oldPrice={
                (oldPriceView?.showOldPrice && oldPriceView.oldPrice) ||
                undefined
              }
              listItems={[
                age,
                `${formatNumber(views)} views`,
                `${countyTown ? `${countyTown}, ` : ''}${county}`,
              ]}
              renderSaveAd={
                (userSaved !== undefined || isPreview) && (
                  <div ref={saveAdRef}>
                    {saveAdInView && (
                      <ShowAndToggleSaveAd
                        adId={id}
                        isUnderlined={false}
                        userSaved={userSaved}
                        isAdSaved={isAdSaved}
                        setIsAdSaved={setIsAdSaved}
                        isViewingOwnAd={isViewingOwnAd}
                        isPreview={isPreview}
                        pulseOnLoad={
                          triggerSaveAdPulseOnLoad &&
                          !hasSavedAdAnimationOccurred &&
                          saveAdInView
                        }
                        hasSavedAdAnimationOccurred={
                          hasSavedAdAnimationOccurred
                        }
                        setHasSavedAdAnimationOccurred={
                          setHasSavedAdAnimationOccurred
                        }
                        subsection={section?.name}
                        sellerType={sellerType}
                      />
                    )}
                  </div>
                )
              }
              renderBoostButton={
                isViewingOwnAd && (
                  <Button
                    ofType="PINK"
                    onClick={() =>
                      user?.id &&
                      section &&
                      section?.id &&
                      UseBoostHandler(
                        {
                          userId: `${user?.id}`,
                          adId: id,
                          setBoostModalData,
                          setIsBoostModalOpen,
                          currency:
                            county && countiesNI.includes(county)
                              ? CURRENCIES.GBP
                              : CURRENCIES.EUR,
                          photoCount: photos ? photos.length : 0,
                          sectionId: section.id,
                          wanted: wanted ?? false,
                          price: price ?? '0',
                          section: section?.displayName,
                        },
                        formatAuthorizationHeader(accessToken),
                      )
                    }
                  >
                    Boost
                  </Button>
                )
              }
              renderEditButton={<ButtonLink href={editAdHREF}>Edit</ButtonLink>}
              isViewingOwnAd={isViewingOwnAd}
              className="common-margin-bottom"
              handleShareAd={() => setIsShareModalOpen(true)}
              stripeEnabled={stripeEnabled}
              deliveryAvailable={deliveryAvailable}
              deliveryFee={delivery?.fee}
              quantity={quantity}
              buyNowSuccess={buyNowSuccess}
              manufacturerApprovedBadge={manufacturerApprovedBadge}
              specialOffer={specialOffer}
              meritsV2={meritsV2}
              greenlightVerified={greenlightVerified}
              herdwatchVerified={herdwatchVerified}
              cardPayments={cardPayments}
              carFinance={finance}
              buttonDisplayText={buttonDisplayText}
              depositEnabled={depositEnabled}
              adAnalytics={adAnalytics}
              priceOnRequest={priceOnRequest}
              isSold={isSold}
            />
            {!isPreview && eligibleForSaveSearch && (
              <RevealSaveSearchPanel
                adId={id}
                className="common-margin-bottom"
                isSearchSaved={savedSearchSaved}
                setIsSearchSaved={setIsSearchSaved}
                baseUrl={baseUrl}
              />
            )}
            {!isPreview && greenlight && (
              <GreenlightPanel
                listBlock={greenlight.listBlock}
                titleGroup={greenlight.titleGroup}
                infoLink={greenlight.infoLink}
                greenlightVerified={greenlightVerified}
                showHistoryCheck={showHistorycheckV2}
                cdnUrl={cdnUrl}
                className="common-margin-bottom"
              />
            )}
            {showElectricCarHubPanel && (
              <ElectricCarsHubPanel className="common-margin-bottom" />
            )}
            <InfoPanel
              title="Detailed Info"
              listItems={['Description']}
              information={description}
              renderAction={
                !isPreview && (
                  <RevealReportAd
                    isViewingOwnAd={isViewingOwnAd}
                    editAdHREF={editAdHREF}
                  />
                )
              }
              className="common-margin-bottom"
              cdnUrl={cdnUrl}
              section={section}
              displayAttributes={displayAttributes}
              servicePartners={servicePartners}
              Link={NextLink}
              renderDfpUnit={
                !isPreview &&
                !displayExpiredForSeo && (
                  <DFPUnit {...AD_DETAILS_DFP_CONFIG.dd_detail_calculator} />
                )
              }
              isPreview={isPreview}
              vehicleFeatures={
                vehicleFeatures && mapVehicleFeatures(vehicleFeatures)
              }
            />
            {dataTargetingProps.dfp?.vertical[0] === 'Marketplace' && (
              <div ref={ref} className="common-margin-bottom">
                <AdSenseUnit unit={AD_DETAILS_AD_SENSE_CONFIG.middle} />
              </div>
            )}
            {!isPreview && (
              <DFPUnit
                className="common-margin-bottom mid-placement-extra-space"
                {...AD_DETAILS_DFP_CONFIG.dd_detail_mid}
              />
            )}
            {!isPreview && (
              <PromoPanelCollection
                breadCrumbSectionName={breadCrumbSectionName}
                sectionName={section?.name}
                cdnUrl={cdnUrl}
                showGreenlight={showHistorycheckV2}
                financeInfo={financeInfo}
                className="common-margin-bottom"
              />
            )}
            {approvedProgramme && (
              <SpecialInfoPanel
                id={approvedProgramme.id}
                hero={approvedProgramme.hero}
                benefits={approvedProgramme.benefits}
                moreInfo={approvedProgramme.moreInfo}
                description={approvedProgramme.description}
                terms={approvedProgramme.terms}
                className="common-margin-bottom"
              />
            )}
            <div ref={mobileStickyCTARefOne}>
              {!isPreview && (
                <MobileStickyFooter
                  position="fixed"
                  active={!mobileStickyCTAinView2 && !mobileStickyCTAinView3}
                  isViewingOwnAd={isViewingOwnAd}
                  data-testid="mobile-sticky-cta"
                  whatsappResponse={whatsappResponse}
                >
                  <Columns>
                    {isViewingOwnAd ? (
                      <Button
                        ofType="PINK"
                        onClick={() =>
                          user?.id &&
                          section &&
                          section?.id &&
                          UseBoostHandler(
                            {
                              userId: `${user?.id}`,
                              adId: id,
                              setBoostModalData,
                              setIsBoostModalOpen,
                              currency:
                                county && countiesNI.includes(county)
                                  ? CURRENCIES.GBP
                                  : CURRENCIES.EUR,
                              photoCount: photos ? photos.length : 0,
                              sectionId: section.id,
                              wanted: wanted ?? false,
                              price: price ?? '0',
                              section: section?.displayName,
                            },
                            formatAuthorizationHeader(accessToken),
                          )
                        }
                        data-testid="boost-button"
                      >
                        Boost
                      </Button>
                    ) : (
                      phoneResponse && (
                        <RevealPhoneNumber
                          id={id}
                          clickSource="mobile-sticky"
                          isPrimaryButton={Boolean(!dealer && !emailResponse)}
                          campaignId={campaignId}
                          showNumberTextVariant
                          sectionName={section?.name}
                          sellerType={sellerType}
                        />
                      )
                    )}
                    {isViewingOwnAd ? (
                      <ButtonLink href={editAdHREF}>Edit</ButtonLink>
                    ) : dealer ? (
                      <RevealDealerEnquiryModal
                        adId={id}
                        text="Enquire"
                        id="dealer-enquiry-btn-footer"
                      />
                    ) : (
                      emailResponse && (
                        <RevealSendMessage
                          adId={id}
                          clickSource="mobile-sticky"
                        />
                      )
                    )}
                  </Columns>
                </MobileStickyFooter>
              )}
              <div ref={ref}>
                {!isPreview && seller && !dealer && !displayExpiredForSeo && (
                  <SellerInfoPanel
                    title={sellerTitle}
                    profileImageUrl={sellerPhoto}
                    renderSendMessage={
                      emailResponse && (
                        <RevealSendMessage
                          adId={id}
                          testId="bottom-panel-send-message"
                          trackingId="contact-seller-send-btn-large-bottom"
                          clickSource="bottom-panel"
                        />
                      )
                    }
                    renderShowPhone={
                      phoneResponse && (
                        <RevealPhoneNumber
                          id={id}
                          clickSource="bottom-panel"
                          isPrimaryButton={Boolean(!dealer && !emailResponse)}
                          campaignId={campaignId}
                          sectionName={section?.name}
                          sellerType={sellerType}
                        />
                      )
                    }
                    county={seller.county}
                    type={seller.type}
                    sellerUrl={`${baseUrl}/all?userId=${seller.id}`}
                    isTrader={isTrader}
                    verification={seller.verification}
                    registrationDate={seller.registrationDate}
                    adCountStats={seller.adCountStats}
                    avgResponseRate={seller.avgResponseRate}
                    testId="bottom-seller-panel"
                    className="common-margin-bottom"
                  />
                )}
                <OffWhiteWrapper showBackground={showMiniShowroom}>
                  {!isPreview && dealer && (
                    <SimpleErrorBoundary>
                      <DealerPanel
                        title={dealer.name}
                        smallLogo={dealer?.logo?.small}
                        listItems={[
                          dealer.franchiseType,
                          dealer.establishedYear
                            ? `Est ${dealer.establishedYear}`
                            : undefined,
                        ]}
                        affiliateLogo={
                          dealer.type === 'SIMI'
                            ? `${cdnUrl}/images/illustrations/SIMI.svg`
                            : undefined
                        }
                        showRoomImage={dealer?.forecourtPhoto?.medium}
                        largeLogo={dealer?.logo?.medium}
                        renderMakeEnquiry={
                          <RevealDealerEnquiryModal
                            adId={id}
                            id="dealer-enquiry-btn-dealer-panel"
                          />
                        }
                        renderShowPhone={
                          phoneResponse && (
                            <RevealPhoneNumber
                              id={id}
                              clickSource="dealer-panel"
                              isPrimaryButton={Boolean(
                                !dealer && !emailResponse,
                              )}
                              campaignId={campaignId}
                              sectionName={section?.name}
                              sellerType={sellerType}
                            />
                          )
                        }
                        renderVisitShowRoom={
                          dealer.showroomUrl && (
                            <ButtonLink
                              ofType="SECONDARY"
                              NextLink={NextLink}
                              href={dealer.showroomUrl}
                            >
                              Visit Showroom
                            </ButtonLink>
                          )
                        }
                        location={formatAddress(
                          dealer.address,
                          dealer.enhancedAddress,
                          true,
                        )}
                        address={formatAddress(
                          dealer.address,
                          dealer.enhancedAddress,
                        )}
                        latitude={dealer?.latitude}
                        longitude={dealer?.longitude}
                        openingHours={dealer.enhancedOpeningHours?.days}
                        websiteUrl={dealer.websiteURL}
                        Link={NextLink}
                        linkHref={dealer.showroomUrl}
                        isViewingOwnAd={isViewingOwnAd}
                        className="common-margin-bottom"
                        googleReviews={dealer?.googleReview}
                        cdnUrl={cdnUrl}
                        adId={id}
                      />
                    </SimpleErrorBoundary>
                  )}
                  {!isPreview && relatedAds?.length && (
                    <RelatedAds
                      className="common-margin-bottom"
                      keepPaddingOnDesktop={showMiniShowroom}
                    >
                      {showMiniShowroom ? (
                        <OurStockHeader>
                          <OurStockHeading>Our Stock</OurStockHeading>
                          <Link
                            ofType="SECONDARY"
                            href={dealer!.showroomUrl}
                            NextLink={NextLink}
                          >
                            Total Stock: {dealer!.totalAds}
                          </Link>
                        </OurStockHeader>
                      ) : (
                        <InfoTitle
                          title="You may also like"
                          listItems={['Similar items in your price range']}
                          className="common-margin-bottom"
                        />
                      )}
                      <RelatedAdsGrid
                        adId={id}
                        relatedAds={relatedAds}
                        cdnUrl={cdnUrl}
                        domain={baseUrl}
                        variant={showMiniShowroom ? 'ENHANCED' : 'SIMPLE'}
                        orientation={
                          showMiniShowroom ? 'HORIZONTAL' : 'VERTICAL'
                        }
                        testId="related-ads"
                      />

                      {!isPreview && showMiniShowroom && (
                        <SeeAllStock>
                          <OptionalNextLink
                            href={dealer!.showroomUrl}
                            Link={NextLink}
                          >
                            <Link ofType="SECONDARY" isFakeLink>
                              See All Stock
                            </Link>
                          </OptionalNextLink>
                        </SeeAllStock>
                      )}
                    </RelatedAds>
                  )}
                </OffWhiteWrapper>
                <SLegalInfoPanel />
              </div>
            </div>
          </div>
          {!isPreview && (
            <SideColumn className="columns large-4">
              {(seller?.traderName ?? seller?.name) &&
                !displayExpiredForSeo && (
                  <SSideSellerInfoPanel
                    renderSendMessage={
                      dealer ? (
                        <RevealDealerEnquiryModal
                          adId={id}
                          id="dealer-enquiry-btn-side-panel"
                        />
                      ) : (
                        emailResponse && (
                          <RevealSendMessage
                            adId={id}
                            trackingId="contact-seller-send-btn-large-top"
                            clickSource="side-panel"
                          />
                        )
                      )
                    }
                    renderShowPhone={
                      (phoneResponse && (
                        <RevealPhoneNumber
                          id={id}
                          isPrimaryButton={Boolean(!dealer && !emailResponse)}
                          clickSource="side-panel"
                          campaignId={campaignId}
                          sectionName={section?.name}
                          sellerType={sellerType}
                        />
                      )) ||
                      undefined
                    }
                    title={sellerTitle}
                    isTrader={isTrader}
                    county={county}
                    type={seller.type}
                    verification={seller.verification}
                    sellerUrl={`${baseUrl}/all?userId=${seller.id}`}
                    testId="side-seller-panel"
                    className="common-margin-bottom"
                    isDealer={isDealer}
                    showroomUrl={dealer?.showroomUrl}
                    illustration={sellerPhoto}
                    dealerReviews={dealer?.googleReview?.userRatingsTotal}
                    dealerRating={dealer?.googleReview?.rating}
                  />
                )}

              <Sticky>
                <DFPUnit
                  className="common-margin-bottom"
                  {...AD_DETAILS_DFP_CONFIG.dd_detail_side}
                />

                <PromoPanelCollection
                  breadCrumbSectionName={breadCrumbSectionName}
                  sectionName={section?.name}
                  cdnUrl={cdnUrl}
                  showGreenlight={showHistorycheckV2}
                  financeInfo={financeInfo}
                  displayOnDesktopOnly={true}
                  className="common-margin-bottom"
                />

                {buyerTips && (
                  <SHtmlInfoPanel
                    heading={buyerTips.title}
                    htmlArray={buyerTips.values}
                  />
                )}
              </Sticky>
            </SideColumn>
          )}
        </div>
        <div ref={mobileStickyCTARefTwo}>
          {!isPreview &&
            seoData?.internalLinks &&
            seoData?.internalLinks.length !== 0 && (
              <SeoContainer>
                <div className="row small-collapse medium-uncollapse">
                  <div className="columns small-12">
                    <SInternalLinks
                      title="You may also be interested in"
                      links={seoData.internalLinks}
                    />
                  </div>
                </div>
              </SeoContainer>
            )}

          {!isPreview && promoSlot && (
            <FeaturedDealerContainer>
              <div className="row small-collapse medium-uncollapse">
                <div className="columns small-12">
                  <FeaturedDealer
                    domain={baseUrl}
                    cdnUrl={cdnUrl}
                    dealerLogo={promoSlot.largeLogoUrl ?? promoSlot.logoUrl}
                    title={promoSlot.display}
                    listItems={[
                      promoSlot.dealerType,
                      `Total Stock: ${promoSlot.totalAdCount} Ads`,
                    ]}
                    showRoomUrl={promoSlot.showroomUrl}
                    showRoomText="See Showroom"
                    ads={promoSlot.ads}
                  />
                </div>
              </div>
            </FeaturedDealerContainer>
          )}

          {/* Below are components not initially visible on the page */}
          {!isPreview && (seller?.traderName ?? seller?.name) && (
            <AdDetailsComponentsThatAreNotInitiallyVisible
              baseUrl={baseUrl}
              cdnUrl={cdnUrl}
              adId={id}
              title={sellerTitle}
              sectionName={section?.name}
              illustration={sellerPhoto}
              county={county}
              type={seller.type}
              isTrader={isTrader}
              verification={seller.verification}
              friendlyUrl={friendlyUrl}
              dealer={dealer}
              sellerName={sellerTitle}
              adTitle={header}
              refreshTopAndSideDFPUnit={refreshTopAndSideDFPUnit}
              savedSearchData={
                eligibleForSaveSearch && displayAttributes
                  ? generateSaveSearchModalData(
                      displayAttributes,
                      price,
                      currency,
                      section,
                      county,
                      wanted,
                    )
                  : undefined
              }
              createSavedSearchFromAd={createSavedSearchFromAd}
              savedSearchSaving={savedSearchSaving}
              savedSearchError={savedSearchError}
              savedSearchSaved={savedSearchSaved}
            />
          )}
        </div>

        {whatsappResponse && id && (
          <ContactDealership
            adId={id}
            dealer={dealer}
            sellerType={sellerType}
            emailResponse={emailResponse}
            phoneResponse={phoneResponse}
            sectionName={section?.name}
            campaignId={campaignId}
          />
        )}
      </DFPSlotsProvider>
    </AdDetailsWrapper>
  );
}

export { AdDetails };
